import React from "react";
import { Card, CardContent, Typography, Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import styled from "@emotion/styled";
import { shape } from "@mui/system";
import UserMenuChip from "./UserMenuChip";
import ActiveChip from "./ActiveChip";
import { Language as WebsiteIcon, Schedule as BillingIcon, Person as PersonIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useHasPermission } from "../utils/permissionUtils";
import { statusColor } from "../utils/statusColors";
import { sanitizeUrl } from "../utils/navigateUtils";

const CardStyled = styled(Card, { shouldForwardProp: (prop) => prop !== 'inactive' })(({ theme, inactive }) => ({
    position: "relative",
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[3],
    borderRadius: shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: "visible",
    "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: theme.shadows[8],
        borderColor: theme.palette.primary.light,
    },
    ...(inactive && {
        borderLeft: `6px solid ${theme.palette.warning.main}`,
        background: alpha(theme.palette.warning.main, 0.04),
    }),
    // "&::before": {
    //     content: '""',
    //     position: "absolute",
    //     top: 0,
    //     left: shape.borderRadius,
    //     right: shape.borderRadius,
    //     height: 4,
    //     background: "linear-gradient(90deg, #FFFFFF, #F7951B)",
    //     borderRadius: shape.borderRadius * 2,
    // },
}));

const ClientAvatarBox = styled(Box)(({ theme }) => ({
    position: "absolute",
    top: theme.spacing(2),
    right: theme.spacing(2),
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: theme.spacing(0),
}));

const InfoRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    "& .MuiSvgIcon-root": {
        fontSize: theme.spacing(2),
        color: theme.palette.text.secondary,
    },
}));

const ClientName = styled(Typography)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: theme.spacing(1),
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.h6.fontSize,
    color: theme.palette.text.primary,
    marginBottom: 4,
    lineHeight: 1.3,
    cursor: "pointer",
    "&:hover": {
        color: theme.palette.primary.dark,
    },
}));

const WebsiteLink = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.md,
    marginBottom: theme.spacing(2),
    textDecoration: "none",
    "&:hover": {
        color: theme.palette.text.primary,
        textDecoration: "underline",
    },
}));

const MetricsBox = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const MetricBox = styled(Box)(({ theme }) => ({
    background: alpha(theme.palette.primary.main, 0.05),
    borderRadius: 12,
    padding: "12px 16px",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
}));

const MetricValue = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.size.lg,
    color: theme.palette.text.primary,
    marginBottom: 2,
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontWeight: theme.typography.fontWeightMedium,
}));

const StatusBox = styled(Box)(({ theme }) => ({
    width: theme.spacing(2),
    minWidth: theme.spacing(2),
    height: theme.spacing(2),
    borderRadius: "50%",
    [theme.breakpoints.up("sm")]: {
        width: theme.spacing(2),
        height: theme.spacing(2),
    },
}));

const ClientCard = ({ client, HandleReAssignClientManager }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission("canSeeInternal");
    const { name, slug, active, privacy, billingTerms, website, accountsNumber, accounts, clientManager } = client;
    const accessibleAccountsCount = Array.isArray(accounts) ? accounts.length : accountsNumber;

    const formatWebsite = (url) => {
        return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
    };

    const handleWebsiteClick = (e) => {
        e.stopPropagation();
        window.open(sanitizeUrl(website), "_blank");
    };
    
    return (
        <CardStyled inactive={!active}>
            <CardContent>
                <ClientAvatarBox>
                    <ActiveChip status={active ? "Active" : "Inactive"} />
                </ClientAvatarBox>
                <ClientName variant="h6" onClick={() => navigate(`/client/${slug}`)}>
                    {privacy !== "Public" && <StatusBox bgcolor={statusColor(theme, "privacy", privacy)} />}
                    {name}
                </ClientName>

                {website ? (
                    <WebsiteLink component="a" href={sanitizeUrl(website)} target="_blank" onClick={handleWebsiteClick}>
                        <InfoRow>
                            <WebsiteIcon />
                            {formatWebsite(website)}
                        </InfoRow>
                    </WebsiteLink>
                ) : (
                    <WebsiteLink component="div">
                        <InfoRow>
                            <WebsiteIcon />
                        </InfoRow>
                    </WebsiteLink>
                )}

                {((clientManager !== undefined && clientManager !== null) || canSeeInternal) && (
                    <InfoRow>
                        <PersonIcon />
                        <Typography variant="subtitle2" color="textSecondary">
                            Client Relationship Manager:{" "}
                        </Typography>
                        <UserMenuChip user={clientManager} menuItems={{ reAssign: true }} onReAssign={() => HandleReAssignClientManager(client._id)} />
                    </InfoRow>
                )}

                <MetricsBox>
                    <MetricBox>
                        <MetricValue>{accessibleAccountsCount}</MetricValue>
                        <MetricLabel>Accounts</MetricLabel>
                    </MetricBox>

                    <MetricBox>
                        <MetricValue>{billingTerms.type}</MetricValue>
                        <MetricLabel>Billing</MetricLabel>
                    </MetricBox>
                </MetricsBox>

                {billingTerms.type === "Retainer" && (
                    <InfoRow>
                        <BillingIcon />
                        <Typography variant="body2" color="text.secondary">
                            {billingTerms.hours} Hrs {billingTerms.frequency} / {billingTerms.overage ? "Overage Allowed" : "No Overage"}
                        </Typography>
                    </InfoRow>
                )}
            </CardContent>
        </CardStyled>
    );
};

export default ClientCard;
